import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Upload,
  FileVideo,
  Loader2,
  CheckCircle2,
  Clock,
  ChevronRight,
} from 'lucide-react'
import { useJobsStore } from '../../stores/jobs'
import type { Job, JobStatus } from '../../lib/api'

function StatusBadge({ status }: { status: JobStatus }) {
  const map: Record<JobStatus, string> = {
    queued: 'badge-pending',
    processing: 'badge-processing',
    completed: 'badge-completed',
    failed: 'badge-failed',
    cancelled: 'badge-pending',
  }
  return <span className={map[status]}>{status}</span>
}

function StatCard({
  label,
  value,
  icon,
}: {
  label: string
  value: number
  icon: React.ReactNode
}) {
  return (
    <div className="card flex items-center gap-4">
      <div className="w-10 h-10 bg-alk-black flex items-center justify-center text-alk-red flex-shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-2xl font-display font-bold text-alk-black leading-none">
          {value}
        </div>
        <div className="section-label mt-0.5">{label}</div>
      </div>
    </div>
  )
}

function JobRow({ job, onClick }: { job: Job; onClick: () => void }) {
  const date = new Date(job.createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <tr
      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={onClick}
    >
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <FileVideo size={15} className="text-alk-gray flex-shrink-0" />
          <span className="text-sm font-medium text-alk-black truncate max-w-xs">
            {job.title ?? job.filename}
          </span>
        </div>
      </td>
      <td className="py-3 px-4">
        <StatusBadge status={job.status} />
      </td>
      <td className="py-3 px-4">
        {job.status === 'processing' ? (
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-200 h-1.5 w-24">
              <div
                className="bg-alk-red h-full transition-all duration-300"
                style={{ width: `${job.progress}%` }}
              />
            </div>
            <span className="text-xs text-alk-gray">{job.progress}%</span>
          </div>
        ) : job.status === 'completed' ? (
          <span className="text-xs text-alk-gray">100%</span>
        ) : (
          <span className="text-xs text-alk-gray">—</span>
        )}
      </td>
      <td className="py-3 px-4 text-xs text-alk-gray whitespace-nowrap">{date}</td>
      <td className="py-3 px-4">
        <button className="flex items-center gap-1 text-xs text-alk-red font-display uppercase tracking-wide hover:underline">
          View <ChevronRight size={13} />
        </button>
      </td>
    </tr>
  )
}

export function DashboardPage() {
  const navigate = useNavigate()
  const { jobs, isLoading, fetchJobs } = useJobsStore()

  useEffect(() => {
    void fetchJobs()
  }, [fetchJobs])

  const totalUploads = jobs.length
  const activeJobs = jobs.filter((j) => j.status === 'processing' || j.status === 'queued').length
  const completedJobs = jobs.filter((j) => j.status === 'completed').length

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h1 className="page-title">Dashboard</h1>
        <button
          onClick={() => navigate('/upload')}
          className="btn-primary flex items-center gap-2"
        >
          <Upload size={15} />
          New Upload
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatCard label="Total Uploads" value={totalUploads} icon={<FileVideo size={18} />} />
        <StatCard label="Active Jobs" value={activeJobs} icon={<Loader2 size={18} />} />
        <StatCard label="Completed" value={completedJobs} icon={<CheckCircle2 size={18} />} />
      </div>

      {/* Jobs table */}
      <div className="card p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="font-display text-base font-bold uppercase tracking-wide text-alk-black">
            Recent Jobs
          </h2>
          <span className="section-label">{jobs.length} total</span>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-alk-gray" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-12 h-12 bg-gray-100 flex items-center justify-center mb-3">
              <FileVideo size={24} className="text-alk-gray" />
            </div>
            <p className="font-display text-lg uppercase tracking-wide text-alk-black mb-1">
              No jobs yet
            </p>
            <p className="text-sm text-alk-gray mb-4">
              Upload a video to get started.
            </p>
            <button
              onClick={() => navigate('/upload')}
              className="btn-primary flex items-center gap-2"
            >
              <Upload size={14} />
              New Upload
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="py-2.5 px-4 text-left section-label">File</th>
                  <th className="py-2.5 px-4 text-left section-label">Status</th>
                  <th className="py-2.5 px-4 text-left section-label">Progress</th>
                  <th className="py-2.5 px-4 text-left section-label">Date</th>
                  <th className="py-2.5 px-4 text-left section-label">Action</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <JobRow
                    key={job.id}
                    job={job}
                    onClick={() => navigate(`/jobs/${job.id}`)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick link to upload */}
      {jobs.length > 0 && (
        <div className="mt-4 flex items-center gap-2 text-xs text-alk-gray">
          <Clock size={12} />
          <span>Jobs are retained for 90 days.</span>
        </div>
      )}
    </div>
  )
}
